// src/pages/locations/formErrors.ts

/**
 * Validation errors keyed by field name. Empty object = the form is valid.
 */
export type FieldErrors = Record<string, string>;

type ApiValidationPayload = {
  message?: string;
  errors?: {
    formErrors?: string[];
    fieldErrors?: Record<string, string[]>;
  };
};

function payloadOf(error: unknown): ApiValidationPayload | null {
  if (!error || typeof error !== "object") return null;

  // The API client rejects with Error & { status, payload }.
  const payload = (error as { payload?: unknown }).payload;
  if (payload && typeof payload === "object") return payload as ApiValidationPayload;

  return null;
}

/** Server-side validation errors (422), mapped onto form fields. */
export function apiFieldErrors(error: unknown): FieldErrors {
  const fieldErrors = payloadOf(error)?.errors?.fieldErrors;
  if (!fieldErrors) return {};

  const result: FieldErrors = {};
  for (const [field, messages] of Object.entries(fieldErrors)) {
    const first = Array.isArray(messages) ? messages[0] : undefined;
    if (first) result[field] = first;
  }
  return result;
}

/** Headline message for a failed save, ignoring per-field detail. */
export function apiErrorSummary(error: unknown): string | null {
  if (!error) return null;

  const payload = payloadOf(error);
  const formError = payload?.errors?.formErrors?.[0];
  if (formError) return formError;

  if (payload?.message) return payload.message;
  if (error instanceof Error && error.message) return error.message;

  return "Something went wrong. Please try again.";
}

/* ----------------------------- Field rules ----------------------------- */

export function requiredText(value: string, label: string): string | undefined {
  return value.trim() ? undefined : `${label} is required.`;
}

export function iso2Rule(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return "Country code is required.";
  if (!/^[A-Za-z]{2}$/.test(trimmed))
    return "Use the 2-letter ISO code, e.g. IN.";
  return undefined;
}

export function maxLengthRule(
  value: string,
  max: number,
  label: string
): string | undefined {
  return value.trim().length > max
    ? `${label} must be ${max} characters or fewer.`
    : undefined;
}

export function coordinateRule(
  value: string,
  bound: number,
  label: string
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined; // optional

  const n = Number(trimmed);
  if (!Number.isFinite(n)) return `${label} must be a number.`;
  if (Math.abs(n) > bound)
    return `${label} must be between -${bound} and ${bound}.`;

  return undefined;
}

/** Drops undefined entries so `Object.keys(...).length` means "has errors". */
export function collectErrors(
  entries: Record<string, string | undefined>
): FieldErrors {
  const result: FieldErrors = {};
  for (const [field, message] of Object.entries(entries)) {
    if (message) result[field] = message;
  }
  return result;
}
